<template>
  <div>
    <div v-if="isLoading" class="text-center py-5 text-secondary">
      {{ $t("loadingData") }}
    </div>

    <template v-if="customer && !isLoading">
      <section>
        <div class="d-flex flex-row align-items-center justify-content-between mb-4">
          <h5 class="m-0">{{ $t("customerInfo") }}</h5>
          <div>
            <b-button class="mr-3" variant="outline-primary" v-if="isManager() || isUserAdmin()" :to="'/customers/edit/' + id">
              {{ $t("update") }}
            </b-button>
            <b-button class="mr-3" variant="outline-danger" v-if="isManager() || isUserAdmin()" @click="onClickDeleteCustomer">
              {{ $t("deleteCustomer") }}
            </b-button>
            <b-button class="mr-3" variant="outline-danger" v-if="isManager() || isUserAdmin()" @click="onClickResTrictCustomer">
              {{ $t("restrictCustomer") }}
            </b-button>
            <b-button class="mr-3" variant="outline-danger" v-if="isManager() || isUserAdmin()" @click="onClickWithDrawCustomer">
              {{ $t("withdrawConsent") }}
            </b-button>
          </div>
        </div>
        <b-row class="mb-4 pb-4 border-bottom">
          <b-col lg="6" class="border-right">
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("customerId") }}:</span></b-col>
              <b-col cols="8">{{ customer.customerCode }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("customerName") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customer.name }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("customerType") }}:</span></b-col>
              <b-col cols="8">{{ customerTypeText }}</b-col>
            </b-row>
            <b-row v-if="customer.corporations">
                <b-col cols="4"><span class="text-secondary">{{ $t("underTheUnit") }}:</span></b-col>
                <b-col cols="8">{{ corporationsText }}</b-col>
              </b-row>
              <b-row v-else>
                <b-col cols="4"><span class="text-secondary">{{ $t("ownedBy") }}:</span></b-col>
                <b-col cols="8">{{ idTypeText }}</b-col>
              </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">Email:</span></b-col>
              <b-col cols="8">{{ customer.email }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("phoneNumber") }}:</span></b-col>
              <b-col cols="8">{{ customer.phoneNumber }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("address") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customer.address }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("status") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customerStatusMap[customer.customerStatus] }}</b-col>
            </b-row>
          </b-col>
          <b-col lg="6">
            <b-row v-if="customer.taxCode">
              <b-col cols="4"><span class="text-secondary">{{ $t("taxCode") }}:</span></b-col>
              <b-col cols="8">{{ customer.taxCode }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("documentType") }}:</span></b-col>
              <b-col cols="8">{{ customer.documentTypeName }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("documentNumber") }}:</span></b-col>
              <b-col cols="8">{{ customer.idCard }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("issueDate") }}:</span></b-col>
              <b-col cols="8">{{ localDateFormat(customer.licenseDate) }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("expDate") }}:</span></b-col>
              <b-col cols="8">{{ localDateFormat(customer.licenseExpirationDate) }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("issuePlace") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customer.licensePlace }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("storageCapacity") }}:</span></b-col>
              <b-col cols="8">{{ customer.recordingStorage }} MB</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("note") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customer.description }}</b-col>
            </b-row>
          </b-col>
        </b-row>
      </section>

      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h5 class="m-0">{{ $t("adminAccountList") }} ({{ adminUsers.length }})</h5>
        <b-button v-if="isManager()" variant="outline-primary" v-b-modal.new-customer-admin-modal>
          <b-icon icon="plus"></b-icon> {{ $t("createAdminAccount") }}
        </b-button>
      </div>

      <b-table :items="adminUsers" hover primary-key="id" :fields="tableFields" show-empty>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-if="isManager() || isUserAdmin()" v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button @click="onClickEditAdminUser(data.item)">{{ $t("updateInfo") }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickResetAdminUserPassword(data.item)">{{ $t("resetPassword") }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickUnlockAccount(data.item)">{{ $t("unlockAccount") }}</b-dropdown-item-button>
            <b-dropdown-item-button variant="danger" @click="onClickDeleteAdminUser(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </template>

    <b-modal id="new-customer-admin-modal" :title="$t('createCustomerAdminAccount')"
             dialog-class="min-width-700" hide-footer no-close-on-backdrop>
      <NewCustomerAdminUserModal :customer-id="id" @submitted="fetchCustomer" />
    </b-modal>

    <b-modal id="edit-customer-user-modal" :title="$t('updateAccountInfo')"
             hide-footer no-close-on-backdrop>
      <EditCustomerUserModal :editing-user="editingUser" @submitted="fetchCustomer" />
    </b-modal>

    <b-modal id="reset-password-modal" :title="$t('resetPassword')"
             hide-footer no-close-on-backdrop>
      <ResetPasswordModal :target-user="editingUser" @submitted="fetchCustomer" />
    </b-modal>

  </div>
</template>

<style lang="scss" scoped>
  .customer-info-text {
    font-size: 17px;
    line-height: 175%;

    & > * > :first-child {
      flex-shrink: 0;
      margin-right: 1rem;
    }

    & > * > :nth-child(2) {
      text-align: right;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import NewCustomerAdminUserModal from "@/components/customer/NewCustomerAdminModal.vue";
  import {BvTableFieldArray} from "bootstrap-vue";

  import * as util from "@/service/util";
  import * as userService from "@/service/userService";
  import * as customerService from "@/service/customerService";
  import * as subscriptionPlanService from "@/service/subscriptionPlanService";
  import EditCustomerUserModal from "@/components/customer/user-accounts/EditCustomerUserModal.vue";
  import ResetPasswordModal from "@/components/account/ResetPasswordModal.vue";


  @Component({
    components: { EditCustomerUserModal, NewCustomerAdminUserModal, ResetPasswordModal }
  })
  export default class CustomerDetailsOverview extends BaseComponent {

    readonly customerStatusMap = {
      Active: this.$t("customerStatusActive").toString(),
      Inactive: this.$t("customerStatusInactive").toString(),
      Cancelled: this.$t("customerStatusCancelled").toString()
    };

    readonly customerStatusBadgeVariantMap = {
      Active: "primary",
      Inactive: "secondary",
      Cancelled: "danger"
    };

    id: number = null;

    isLoading = false;
    customer = null;
    subscriptions = [];

    adminUsers = [];
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#"
      },
      {
        key: "name",
        label: this.$t("fullName").toString(),
      },
      {
        key: "username",
        label: "Username",
      },
      {
        key: "email",
        label: "Email"
      },
      {
        key: "role",
        label: this.$t("role").toString(),
        formatter: value => ({ Customer: this.$t("roleAdmin").toString(), User: this.$t("roleUser").toString() }[value] || value)
      },
      {
        key: "createdAt",
        label: this.$t("createdDate").toString()
      },
      {
        key: "actions",
        label: ""
      }
    ];

    editingUser = null;


    mounted() {
      this.id = +this.$route.params.id || null;
      this.fetchCustomer();
    }

    async fetchCustomer() {
      try {
        this.isLoading = true;

        const fetchCustomerPromise = customerService.getDetails(this.id);
        const fetchDocumentTypesPromise = customerService.getDocumentTypes();
        const fetchSubscriptionsPromise = subscriptionPlanService.getCustomerSubscription(this.id);
        const fetchAdminUsersPromise = userService.getCustomerAdmins(this.id);

        this.customer = await fetchCustomerPromise;
        const documentTypes = await fetchDocumentTypesPromise;
        this.customer.documentTypeName = documentTypes.find(it => it.id === this.customer.documentTypeId)?.title;

        this.subscriptions = await fetchSubscriptionsPromise;
        this.adminUsers = await fetchAdminUsersPromise;

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoading = false;
      }
    }


    get activeSubscription() {
      return this.subscriptions?.find(it => it.isActive);
    }

    get customerTypeText(): string {
      const type = this.customer?.customerType?.toLowerCase();
      return  this.$t(type).toString();
    }
    get corporationsText(): string {
      const type = this.customer?.corporations?.toLowerCase();
      return  this.$t(type).toString();
    }
    get idTypeText(): string {
      const type = this.customer?.idType?.toLowerCase();
      return  this.$t(type).toString();
    }

    onClickEditAdminUser(user) {
      this.editingUser = user;
      this.$bvModal.show("edit-customer-user-modal");
    }

    onClickResetAdminUserPassword(user) {
      this.editingUser = user;
      this.$bvModal.show("reset-password-modal");
    }

    async onClickUnlockAccount(user) {
      const modalMessage = this.$t("unlockAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'primary',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await userService.unlockAccount(user.id);
          this.fetchCustomer();
          this.toastSuccessDelay(this.$t("unlockAccountSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }
    

    async onClickDeleteAdminUser(user) {
      const modalMessage = this.$t("deleteAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await userService.deleteUser(user.id);
          this.fetchCustomer();
          this.toastSuccessDelay(this.$t("deleteAccountSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

    async onClickDeleteCustomer() {
      const modalMessage = this.$t("deleteCustomerConfirmation", { name: this.customer.name }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await customerService.deleteCustomer(this.id);
          this.$router.replace("/customers");
          this.toastSuccessDelay(this.$t("deleteCustomerSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

    async onClickResTrictCustomer() {
      const modalMessage = this.$t("restrictCustomerConfirmation", { name: this.customer.name }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await customerService.deleteCustomer(this.id);
          this.$router.replace("/customers");
          this.toastSuccessDelay(this.$t("deleteCustomerSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }
    async onClickWithDrawCustomer() {
      const modalMessage = this.$t("withDrawCustomerConfirmation", { name: this.customer.name }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await customerService.deleteCustomer(this.id);
          this.$router.replace("/customers");
          this.toastSuccessDelay(this.$t("deleteCustomerSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }
  }
</script>
